import * as React from 'react';
import { useCubeQuery } from '@cubejs-client/react';
import { DateRangePickerValue } from 'ui/MuiDateRangePicker';
import { eachDayOfInterval, eachMonthOfInterval, eachWeekOfInterval, format, isAfter } from 'date-fns';

import {
    REVIEWS_DISTRIBUTION_MEASURES,
    getReviewsAnalyticsTimeDimensions,
    EReviewsMeasureView,
    getReviewsAnalyticsFilters,
    TimeGranularity,
    REVIEWS_DISTRIBUTION_MEASURES_MAP,
    ReviewsDistributionMeasureKey
} from '../reviewsAnalytics';

export interface ReviewTrendsData {
    distribution: Record<string, { date: string; count: number }[]>;
    loading: boolean;
    dates: Date[];
}

export function useReviewTrendsQueries(
    dateRange: DateRangePickerValue,
    selectedLocationIds: string[],
    granularity: TimeGranularity
) {
    const query = useCubeQuery({
        measures: REVIEWS_DISTRIBUTION_MEASURES,
        dimensions: [],
        timeDimensions: getReviewsAnalyticsTimeDimensions(
            EReviewsMeasureView.REVIEWS_METRICS,
            dateRange,
            granularity
        ),
        filters: getReviewsAnalyticsFilters(EReviewsMeasureView.REVIEWS_METRICS, selectedLocationIds)
    });

    const volumeData = React.useMemo<ReviewTrendsData>(() => {
        const resultSeries = query.resultSet?.series();
        if (!resultSeries) {
            return {
                distribution: {},
                dates: [],
                loading: query.isLoading
            };
        }
        let dates: Date[];
        // date-fns v3+ returns a reversed array for inverted intervals instead of throwing;
        // guard against that so the trend chart can't render backwards.
        if (isAfter(dateRange.startDate, dateRange.endDate)) {
            return {
                distribution: {},
                dates: [],
                loading: query.isLoading
            };
        }
        switch (granularity) {
            case 'week':
                dates = eachWeekOfInterval(
                    { start: dateRange.startDate, end: dateRange.endDate },
                    { weekStartsOn: 1 }
                );
                break;
            case 'month':
                dates = eachMonthOfInterval({ start: dateRange.startDate, end: dateRange.endDate });
                break;
            default:
                dates = eachDayOfInterval({ start: dateRange.startDate, end: dateRange.endDate });
        }
        const data: Record<string, { date: string; count: number }[]> = {};
        for (const series of resultSeries) {
            const rating =
                REVIEWS_DISTRIBUTION_MEASURES_MAP[series.key as ReviewsDistributionMeasureKey].value;
            data[rating] = series.series.map(dataPoint => ({
                date: format(new Date(dataPoint.x), 'yyyy-MM-dd'),
                count: dataPoint.value
            }));
        }
        return {
            distribution: data,
            loading: query.isLoading,
            dates
        };
    }, [dateRange.startDate, dateRange.endDate, granularity, query]);

    return {
        volumeData
    };
}
